import { queryOptions } from '@tanstack/react-query';

import { fetchBackend } from '../../apiClient';

export type GrafanaConfigurationStatus = 'active' | 'inactive' | 'error';

export type GrafanaConfiguration = {
  id: string;
  instanceUrl: string;
  serviceAccountDisplayName: string;
  serviceAccountToken: string; // base64-encoded string from backend []byte
  status: GrafanaConfigurationStatus;
  isSelfHosted: boolean;
};

export type GrafanaConfigurationForCreate = Omit<
  GrafanaConfiguration,
  'id' | 'status'
>;

export type GrafanaConfigurationForList = Omit<
  GrafanaConfiguration,
  'serviceAccountToken'
>;

export const monitoringGrafanaQueries = {
  all: () => ['monitoring', 'grafana'],
  byProject: (projectId: string) => [
    ...monitoringGrafanaQueries.all(),
    projectId,
  ],
  list: (projectId: string) =>
    queryOptions({
      queryKey: [...monitoringGrafanaQueries.byProject(projectId), 'list'],
      queryFn: async () => {
        const configurations = await listGrafanaConfigurations(projectId);

        return configurations;
      },
    }),
  details: (projectId: string) => [
    ...monitoringGrafanaQueries.byProject(projectId),
    'details',
  ],
  detail: (projectId: string, id: string) =>
    queryOptions({
      queryKey: [...monitoringGrafanaQueries.details(projectId), id],
      queryFn: async () => {
        const configuration = await getGrafanaConfiguration(projectId, id);

        return configuration;
      },
    }),
} as const;

const listGrafanaConfigurations = async (
  projectId: string,
): Promise<GrafanaConfigurationForList[]> => {
  try {
    const response = await fetchBackend(
      `/monitoring/${projectId}/grafana-configurations`,
      {
        method: 'GET',
      },
    );

    if (!response.ok) {
      // If the API endpoint doesn't exist (404) or other errors, return empty array
      // This allows the frontend to work while backend is being developed
      if (response.status === 404) {
        console.warn(
          'Grafana API endpoint not yet implemented, returning empty list',
        );

        return [];
      }
      throw new Error(`Error: ${response.statusText}`);
    }

    const data = (await response.json()) as {
      grafanaConfigurations: GrafanaConfigurationForList[];
    };

    return data.grafanaConfigurations;
  } catch (error) {
    // Handle network errors or other issues gracefully
    console.warn('Grafana API not available:', error);

    return [];
  }
};

const getGrafanaConfiguration = async (
  projectId: string,
  id: string,
): Promise<GrafanaConfiguration> => {
  try {
    const response = await fetchBackend(
      `/monitoring/${projectId}/grafana-configurations/${id}`,
      {
        method: 'GET',
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const data = (await response.json()) as {
      grafanaConfiguration: GrafanaConfiguration;
    };

    return data.grafanaConfiguration;
  } catch (error) {
    console.warn('Grafana API not available:', error);
    throw error; // Re-throw for detail queries as they need to fail
  }
};

export const deleteGrafanaConfiguration = async (
  projectId: string,
  id: string,
): Promise<void> => {
  try {
    const response = await fetchBackend(
      `/monitoring/${projectId}/grafana-configurations/${id}`,
      {
        method: 'DELETE',
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }
  } catch (error) {
    console.warn('Grafana API not available:', error);
    throw error; // Re-throw for mutations as they need to fail
  }
};

export const upsertGrafanaConfiguration = async (
  projectId: string,
  payload: GrafanaConfigurationForCreate,
  id?: string,
): Promise<{ message: string; id: string }> => {
  try {
    if (id !== undefined) {
      const response = await fetchBackend(
        `/monitoring/${projectId}/grafana-configurations/${id}`,
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const data = (await response.json()) as { message: string; id: string };

      return data;
    }

    const response = await fetchBackend(
      `/monitoring/${projectId}/grafana-configurations`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const data = (await response.json()) as { message: string; id: string };

    return data;
  } catch (error) {
    console.warn('Grafana API not available:', error);
    throw error; // Re-throw for mutations as they need to fail
  }
};
